// ── 상황판 통합 카드 — 큰 카드 패턴 (2026-06-07 재작성) ─────────────────────
//
// 모든 위젯(S/M/L) 동일 큰 카드 디자인:
//   - 배경: 흰색 (bg-white dark:bg-pine-900)
//   - 테두리: border border-{accent}-100 (옅음)
//   - 상단 강조: border-t-[3px] border-t-{accent}-400 (두꺼운 accent)
//   - 헤더: 아이콘 + 라벨(text-[12px] font-bold text-[var(--text-sub)]) + chevron/badge/sub
//   - 본문: 사용자 명세 — text-xs 통일 (S=KPI 1개, M=중간, L=표/지도)
//   - hover: hover:brightness-95
//
// (구) mini row 풍 (bg-{accent}-50) 패턴은 폐기됨. 사용자 명세서대로 큰 카드 풍 통일.
//
// Props:
//   code: 위젯 코드 (sortableId 기본값)
//   accent: 기본 색상 키
//   accentOverride: 사용자 지정 색상 (preference)
//   size:
//     'S' = col-span-1 (KPI/단순 카운트, 큰 숫자 1개)
//     'M' = col-span-2 (작은 카드 — chevron 금액 / 3컬럼 통계 / multi-line)
//     'L' = col-span-2 lg:col-span-3 (큰 본문 — 표/지도/list)
//   title / icon / badge / sub: 헤더 컨텐츠
//   onClick: 카드 클릭 핸들러 (있으면 cursor-pointer)
//   monthNavigate / month / onMonthChange / monthLabel: chevron 월 이동
//   sortableId: 드래그 정렬용 id (없으면 비활성)
//   children: 본문

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../lib/utils';
import { accentText, CARD_BG, DEFAULT_ACCENT } from './widgetAccent';

// 월 이동 헬퍼 — YYYY-MM ± n
function shiftMonth(ym, delta) {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym;
  const [y, m] = ym.split('-').map(Number);
  const zb = (y * 12) + (m - 1) + delta;
  const ny = Math.floor(zb / 12);
  const nm = (zb % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function defaultMonthLabel(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
  const [, mm] = ym.split('-');
  return `${Number(mm)}월`;
}

// size → grid column-span + row-span 클래스
//   S = col-span-1 row-span-1 (220×220 정사각)
//   M = col-span-2 row-span-1 (460×220 가로 직사각)
//   L = col-span-2 row-span-2 (460×460 정사각)
function sizeSpan(size) {
  if (size === 'S') return 'col-span-1 row-span-1';
  if (size === 'L') return 'col-span-2 row-span-2';
  return 'col-span-2 row-span-1'; // 'M' 기본
}

// size → 카드 outer padding (헤더+본문 통합 컨테이너)
// S=강조 디자인 (px-4 py-4), M=중간, L=조금 더 여유
function sizePadding(size) {
  if (size === 'S') return 'px-4 py-4';
  if (size === 'L') return 'px-4 py-3';
  return 'px-4 py-3'; // 'M'
}

export function UnifiedCard({
  code,
  accent,
  accentOverride,
  size = 'M',
  title,
  icon: Icon,
  badge,
  badgeColor,
  sub,
  onClick,
  monthNavigate = false,
  month,
  onMonthChange,
  monthLabel,
  sortableId,
  className,
  children,
  pulse,
  watermarkIcon: WatermarkIcon,
}) {
  const accentKey = accentOverride || accent || DEFAULT_ACCENT;
  const txt = accentText(accentKey);
  const clickable = Boolean(onClick);

  // sortableId 가 없어도 hook 순서 안정 위해 항상 호출.
  const sortable = useSortable({
    id: sortableId || `__nosort_${code || title || 'x'}`,
    disabled: !sortableId,
  });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const dragStyle = sortableId
    ? { transform: CSS.Transform.toString(transform), transition }
    : undefined;

  const labelFn = typeof monthLabel === 'function' ? monthLabel : defaultMonthLabel;

  const handlePrev = (e) => {
    e.stopPropagation();
    if (!monthNavigate || !onMonthChange) return;
    onMonthChange(shiftMonth(month || '', -1));
  };
  const handleNext = (e) => {
    e.stopPropagation();
    if (!monthNavigate || !onMonthChange) return;
    onMonthChange(shiftMonth(month || '', 1));
  };

  const showHeader = Boolean(title || Icon || badge || sub || monthNavigate);

  return (
    <div
      ref={sortableId ? setNodeRef : undefined}
      style={dragStyle}
      onClick={clickable ? onClick : undefined}
      {...(sortableId ? attributes : {})}
      {...(sortableId ? listeners : {})}
      className={cn(
        sizeSpan(size),
        // 2026-08-25 재스킨(사용자 명세): 색 박스 최소화 — 상단 accent 띠·색 테두리 제거,
        // 런처 카드와 동일한 중립 테두리+둥근 모서리+호버 그림자. accent 는 아이콘·수치 색에만.
        'rounded-2xl border min-w-0 transition-shadow relative overflow-hidden',
        'border-[var(--border)]',
        sizePadding(size),
        // S 사이즈: 라벨 상단 → 큰 값 중앙 → 부가 정보 하단 (justify-between)
        size === 'S' && 'flex flex-col justify-between h-full',
        CARD_BG,
        'hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]',
        clickable && 'cursor-pointer',
        !clickable && !sortableId && 'cursor-default',
        sortableId && !clickable && 'cursor-grab active:cursor-grabbing',
        sortableId && isDragging && 'opacity-50 shadow-lg z-10 ring-2 ring-blue-400',
        className,
      )}
    >
      {WatermarkIcon && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute -bottom-2 -right-2 opacity-10',
            txt,
          )}
        >
          <WatermarkIcon size={88} strokeWidth={2} />
        </span>
      )}
      {showHeader && (
        <div className="flex items-center gap-1.5 mb-2 relative z-[1]">
          {Icon && (
            <span className={cn('flex-shrink-0', txt)}>
              <Icon size={16} strokeWidth={2.25} />
            </span>
          )}
          {title && (
            <span className="text-[12px] font-bold text-[var(--text-sub)] whitespace-nowrap">{title}</span>
          )}
          {monthNavigate && (
            <>
              <button
                type="button"
                aria-label="이전 달"
                onClick={handlePrev}
                onPointerDown={(e) => e.stopPropagation()}
                className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
              >
                <ChevronLeft size={12} />
              </button>
              <span className="text-[10px] text-[var(--text-sub)] shrink-0 tabular-nums">
                {labelFn(month || '')}
              </span>
              <button
                type="button"
                aria-label="다음 달"
                onClick={handleNext}
                onPointerDown={(e) => e.stopPropagation()}
                className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
              >
                <ChevronRight size={12} />
              </button>
            </>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            {sub && <span className="text-xs text-[var(--text-muted)] font-semibold">{sub}</span>}
            {badge && (
              <span className={cn('text-[11px] font-bold px-2 py-0.5 rounded-full', badgeColor || 'bg-pine-900 text-white dark:bg-accent-600')}>{badge}</span>
            )}
            {pulse && (
              // 동적 클래스명 회피 — accent text 색을 currentColor 로 받아 background 에 사용
              <span className={cn('w-2 h-2 rounded-full', txt)} style={{ background: 'currentColor' }} />
            )}
          </div>
        </div>
      )}
      <div className="relative z-[1]">{children}</div>
    </div>
  );
}
